import React, { useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, CheckCircle, XCircle, ArrowRight } from 'lucide-react';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const getPasswordStrength = (password) => {
  let score = 0;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[!@#$%^&*()_+\-=\[\]{};:'",.<>?/\\|`~]/.test(password)) score++;
  return score;
};

const strengthLabels = ['', 'Très faible', 'Faible', 'Moyen', 'Fort', 'Très fort'];
const strengthColors = ['', 'bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-[#32CD32]', 'bg-[#32CD32]'];

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const strength = getPasswordStrength(password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    setLoading(true);
    try {
      await authAPI.resetPassword({ token, password });
      setSuccess(true);
      toast.success('Mot de passe réinitialisé !');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#E8F4FD] flex items-center justify-center py-12 px-4">
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-[#D6E8F0] max-w-md w-full text-center">
          <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="font-display text-2xl font-bold text-[#033871] mb-2">Lien invalide</h1>
          <p className="text-[#5A8BA8]">Ce lien de réinitialisation est invalide ou a expiré.</p>
          <Link to="/forgot-password"><Button className="mt-6 bg-[#0C63BF]">Demander un nouveau lien</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#E8F4FD] flex items-center justify-center py-12 px-4" data-testid="reset-password-page">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex">
            <img src="/assets/padsmash_logo_main.png" alt="PadSmash" className="h-24 sm:h-28 w-auto mx-auto" />
          </Link>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-sm border border-[#D6E8F0]">
          {success ? (
            <div className="text-center" data-testid="reset-success">
              <CheckCircle className="w-16 h-16 text-[#32CD32] mx-auto mb-4" />
              <h1 className="font-display text-2xl font-bold text-[#033871] mb-2">Mot de passe réinitialisé !</h1>
              <p className="text-[#5A8BA8] mb-6">Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.</p>
              <Link to="/login">
                <Button className="btn-primary gap-2">
                  Se connecter <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-display text-2xl font-bold text-[#033871] mb-2">Nouveau mot de passe</h1>
              <p className="text-[#5A8BA8] mb-6">Choisissez un mot de passe sécurisé.</p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="password">Nouveau mot de passe</Label>
                  <div className="relative mt-2">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Min. 8 caractères"
                      required
                      data-testid="reset-password-input"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5A8BA8]">
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {password && (
                    <div className="mt-2 space-y-1.5" data-testid="password-strength">
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map(i => (
                          <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= strength ? strengthColors[strength] : 'bg-gray-200'}`} />
                        ))}
                      </div>
                      <p className={`text-xs font-medium ${strength >= 4 ? 'text-[#32CD32]' : strength >= 3 ? 'text-yellow-600' : 'text-red-500'}`}>
                        {strengthLabels[strength]}
                      </p>
                      <ul className="text-[10px] text-[#5A8BA8] space-y-0.5">
                        <li className={password.length >= 8 ? 'text-[#32CD32]' : ''}>
                          {password.length >= 8 ? '✓' : '○'} Min. 8 caractères
                        </li>
                        <li className={/[A-Z]/.test(password) ? 'text-[#32CD32]' : ''}>
                          {/[A-Z]/.test(password) ? '✓' : '○'} Une majuscule
                        </li>
                        <li className={/\d/.test(password) ? 'text-[#32CD32]' : ''}>
                          {/\d/.test(password) ? '✓' : '○'} Un chiffre
                        </li>
                        <li className={/[!@#$%^&*()_+\-=\[\]{};:'",.<>?/\\|`~]/.test(password) ? 'text-[#32CD32]' : ''}>
                          {/[!@#$%^&*()_+\-=\[\]{};:'",.<>?/\\|`~]/.test(password) ? '✓' : '○'} Un caractère spécial
                        </li>
                      </ul>
                    </div>
                  )}
                </div>

                <div>
                  <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirmez votre mot de passe"
                    className="mt-2"
                    required
                    data-testid="reset-confirm-input"
                  />
                  {confirmPassword && password !== confirmPassword && (
                    <p className="text-xs text-red-500 mt-1">Les mots de passe ne correspondent pas</p>
                  )}
                </div>

                <Button type="submit" className="w-full btn-primary h-12" disabled={loading || strength < 4 || password !== confirmPassword} data-testid="reset-submit">
                  {loading ? 'Réinitialisation...' : 'Réinitialiser le mot de passe'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
